import { useState, useMemo, useCallback } from 'react';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { functionalUpdate } from '@tanstack/react-table';
import { useServerPagination, type ServerPaginationInit } from './useServerPagination';

export type ListSortOrder = 'asc' | 'desc';

export interface OffsetSortListQuery {
  lastKey: string | undefined;
  page: number | undefined;
  sortBy: string | undefined;
  sortOrder: ListSortOrder | undefined;
}

export type OffsetSortPaginationOptions = {
  resolveSortField?: (columnId?: string) => string | undefined;
  initialSorting?: SortingState;
  paginationInit?: ServerPaginationInit;
};

function normalizeOffsetSortOptions(
  resolveSortFieldOrOptions?: ((columnId?: string) => string | undefined) | OffsetSortPaginationOptions
): OffsetSortPaginationOptions {
  if (typeof resolveSortFieldOrOptions === 'function') {
    return { resolveSortField: resolveSortFieldOrOptions };
  }
  return resolveSortFieldOrOptions ?? {};
}

export function useOffsetSortPagination(
  initialPageSize: number,
  resolveSortFieldOrOptions?: ((columnId?: string) => string | undefined) | OffsetSortPaginationOptions
) {
  const opts = normalizeOffsetSortOptions(resolveSortFieldOrOptions);
  const pagination = useServerPagination(initialPageSize, opts.paginationInit);
  const [sorting, setSorting] = useState<SortingState>(() => opts.initialSorting ?? []);

  const sortColumnId = sorting[0]?.id;
  const offsetSortActive = Boolean(sortColumnId);
  const sortOrder: ListSortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  const listQuery = useMemo((): OffsetSortListQuery => {
    if (!sortColumnId) {
      return {
        lastKey: pagination.currentLastKey || undefined,
        page: undefined,
        sortBy: undefined,
        sortOrder: undefined,
      };
    }
    const sortBy = opts.resolveSortField?.(sortColumnId) ?? sortColumnId;
    return {
      lastKey: undefined,
      page: pagination.currentPage,
      sortBy,
      sortOrder,
    };
  }, [
    sortColumnId,
    sortOrder,
    pagination.currentPage,
    pagination.currentLastKey,
    opts.resolveSortField,
  ]);

  const syncCursorLastKey = useCallback(
    (lastKey: string | null | undefined) => {
      if (offsetSortActive) return;
      if (lastKey !== undefined) pagination.storeNextPageKey(lastKey);
    },
    [offsetSortActive, pagination.storeNextPageKey]
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSorting((prev) => functionalUpdate(updater, prev));
      pagination.reset();
    },
    [pagination]
  );

  const onPageSizeChange = useCallback(
    (size: number) => {
      pagination.setPageSize(size);
      pagination.reset();
      setSorting([]);
    },
    [pagination]
  );

  const clearSorting = useCallback(() => setSorting([]), []);

  const canGoNext = useCallback(
    (total: number, responseLastKey: string | null | undefined) =>
      offsetSortActive
        ? (pagination.currentPage + 1) * pagination.pageSize < total
        : pagination.canGoNext ||
          (responseLastKey !== null && responseLastKey !== undefined),
    [
      offsetSortActive,
      pagination.currentPage,
      pagination.pageSize,
      pagination.canGoNext,
    ]
  );

  return {
    pagination,
    sorting,
    offsetSortActive,
    listQuery,
    onSortingChange,
    onPageSizeChange,
    clearSorting,
    syncCursorLastKey,
    canGoNext,
  };
}
